import React, { useMemo, useRef } from "react";
import images from "../../../images";
import { v4 as uuidv4 } from "uuid";
import debounce from "lodash/debounce";
import { useTranslation } from "react-i18next";
import CommonModal from "../../../components/common/Modal/ModalCommon";
import Button from "../../../components/common/Button/Button";

const MCQQuestion = ({
  options: initialOptions,
  setOptions,
  isRequired,
  setIsRequired,
  isAdditionalComments,
  setIsAdditionalComments,
  commentPlaceholder,
  setCommentPlaceholder,
  isCompulsory,
  setIsCompulsory,
}) => {
  const { t } = useTranslation();
  // const [selectedOption, setSelectedOption] = useState(null);
  const confirmDeletedOptionsRef = useRef(null);
  const optionIdToDeleteRef = useRef(null);
  // Normalize options to ensure `text` is always defined
  const options = initialOptions.map((option) => ({
    ...option,
    text: option.text ?? option.option_text ?? "", // Initialize text from option_text if needed
  }));

  const handleAddOption = () => {
    const newOption = {
      id: uuidv4(),
      text: "",
    };
    setOptions([...options, newOption]);
  };

  const isChecked = options.some((option) => option.isOther);

  const handleAddOther = () => {
    if (isChecked) {
      setOptions(options.filter((option) => !option.isOther));
    } else {
      const newOption = {
        id: uuidv4(),
        text: "Other",
        isOther: true,
        checked: false,
      };
      setOptions([...options, newOption]);
    }
  };

  const handleOptionChange = (id, newText) => {
    setOptions(
      options.map((option) =>
        option.id === id ? { ...option, text: newText } : option
      )
    );
  };

  // const handleRemoveOption = (id) => {
  //   setOptions(options.filter((option) => option.id !== id));
  //   if (selectedOption === id) {
  //     setSelectedOption(null);
  //   }
  // };

  // const handleOptionSelect = (id) => {
  //   setSelectedOption(id);
  //   setOptions(
  //     options.map((option) =>
  //       option.id === id
  //         ? { ...option, checked: true }
  //         : { ...option, checked: false }
  //     )
  //   );
  // };

  const handleConfirmDelete = (id) => {
    optionIdToDeleteRef.current = id;
    confirmDeletedOptionsRef.current.openModal();
  };

  const handleRemoveOption = (id) => {
    setOptions(options.filter((option) => option.id !== id));
  };

  const debouncedSetCommentPlaceholder = useMemo(
    () =>
      debounce((value) => {
        setCommentPlaceholder(value);
      }, 50),
    [setCommentPlaceholder]
  );

  const handleCommentPlaceholder = (e) => {
    const value = e.target.value;
    debouncedSetCommentPlaceholder(value);
  };

  return (
    <>
      <div className="space-y-2 mb-4">
        {options.map((option) => (
          <div key={option.id}>
            {option?.isOther !== true && (
              <div className="bg-white rounded-full p-4 flex items-center">
                {/* <input
                  type="radio"
                  name="mcq-option"
                  className="w-5 h-5 text-indigo-600 mr-3"
                  onChange={() => handleOptionSelect(option.id)}
                  checked={selectedOption === option.id}
                /> */}
                <input
                  type="text"
                  value={option.text}
                  onChange={(e) =>
                    handleOptionChange(option.id, e.target.value)
                  }
                  className="flex-1 border-0 focus:ring-0 bg-transparent focus:outline-none"
                  placeholder={t("base.enterOptionText")}
                />
                <button
                  onClick={() => handleConfirmDelete(option.id)}
                  className="ml-2 text-gray-400 hover:text-gray-600"
                >
                  <img
                    src={images.BinIcon}
                    alt="TrashIcon"
                    className="w-5 h-5"
                  />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="space-y-2 mb-4">
        <button
          onClick={handleAddOption}
          className="flex items-center gap-2 text-gray-600 py-2"
        >
          <img src={images.PluscircleIcon} alt="Add" className="w-5 h-5" />
          <span className="text-gray-500">{t("base.addOption")}</span>
        </button>

        <div className="flex items-center gap-2">
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={isChecked}
              onChange={handleAddOther}
            />
            <div className="relative w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
          </label>
          <span className="text-gray-500">
            {t("base.add")} "{t("base.other")}"
          </span>
        </div>
      </div>

      <div className="space-y-4 mb-4">
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer focus:outline-none"
              checked={isRequired}
              onChange={() => setIsRequired(!isRequired)}
            />
            <div className="relative w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
          </label>
          <span className="text-gray-500">{t("base.required")}</span>
        </div>

        <div className="flex items-center gap-2">
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer focus:outline-none"
              checked={isAdditionalComments}
              onChange={() => setIsAdditionalComments(!isAdditionalComments)}
            />
            <div className="relative w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
          </label>
          <span className="text-gray-500">
            {t("base.addAdditionalComments")}
          </span>
        </div>

        {isAdditionalComments && (
          <>
            <div className="bg-white rounded-lg p-4">
              <input
                type="text"
                value={commentPlaceholder}
                onChange={handleCommentPlaceholder}
                className="w-full border-0 focus:ring-0 text-sm text-gray-400 focus:outline-none"
                placeholder={t("base.pleaseElaborate")}
              />
            </div>
            <div className="flex justify-between">
              <div></div>
              <div className="flex items-center gap-2">
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={isCompulsory}
                    onChange={() => setIsCompulsory(!isCompulsory)}
                  />
                  <div className="relative w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
                </label>
                <span className="text-gray-500">{t("base.compulsory")}</span>
              </div>
            </div>
          </>
        )}
      </div>

      <CommonModal
        ref={confirmDeletedOptionsRef}
        actions={(closeModal) => (
          <>
            <Button
              text={t("base.cancel")}
              className="flex-1 shrink gap-2.5 self-stretch px-5 py-3 rounded-xl min-h-[40px] text-white bg-indigo-600"
              onClick={() => closeModal(false)}
            />
            <Button
              text={t("base.confirm")}
              className="flex-1 shrink gap-2.5 self-stretch px-5 py-3 rounded-xl min-h-[40px] bg-white border border-solid border-stone-200 text-neutral-500"
              variant="primary"
              onClick={() => {
                handleRemoveOption(optionIdToDeleteRef.current);
                closeModal(false);
              }}
            />
          </>
        )}
      >
        <div className="flex flex-col justify-between gap-2">
          <p className="text-sm">
            {t("quest.onDemand.areYouSureYouWantToDeletedOptions")}
          </p>
        </div>
      </CommonModal>
    </>
  );
};

export default MCQQuestion;
